import { FormField } from 'ui/formField';
import { BasicSelect, BasicOption } from 'ui/selects';
import { PushContentDefinition } from 'modules/graphql/types';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from 'core/userContext';
import { LabelWithLink } from 'modules/templateEditor/common';
import { RestrictionBadge } from '../RestrictionBadge';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { IOS_INTERRUPTION_LEVELS, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { formatInterruptionTypes, getIsEditMode } from '../../common/utils';
import { StyledTooltip } from '../styles';

/*
  A component used to select the interruption level
  for Push templates.
*/
export const InterruptionSelect = () => {
  const id = 'push-settings-select-interruption-level';
  const { view } = useQueryParamContext();
  const featureFlags = useSelector(selectFeatureFlags);
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const options = formatInterruptionTypes(
    IOS_INTERRUPTION_LEVELS,
    featureFlags.IOSCriticalInterruptionLevel,
  );
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const getReadOnlyText = () => {
    if (pushData.isSilentPush) return SETTINGS_DISPLAY_CONTENT.deliverSilently.silenced;
    return (
      options.find((val) => val.value === pushData.interruptionLevel)?.label ||
      SETTINGS_DISPLAY_CONTENT.unsetText
    );
  };

  return (
    <FormField
      isDisabled={pushData.isSilentPush}
      helpText={
        isEditModeVisible ? (
          <LabelWithLink
            text={SETTINGS_DISPLAY_CONTENT.interruptionLevel.helpText}
            url={SETTINGS_DISPLAY_CONTENT.interruptionLevel.helpLink.link}
            openInNewTab
          />
        ) : (
          ''
        )
      }
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.interruptionLevel.labelText}
      restrictionText={
        <RestrictionBadge
          text={SETTINGS_DISPLAY_CONTENT.interruptionLevel.restriction}
          hasTooltip={true}
          tooltipText={SETTINGS_DISPLAY_CONTENT.iOSTooltip}
        />
      }
    >
      {isEditModeVisible ? (
        <StyledTooltip
          isEnabled={pushData.isSilentPush}
          tooltipContent={SETTINGS_DISPLAY_CONTENT.deliverSilently.restriction}
        >
          <BasicSelect
            data-test={id}
            id={id}
            isDisabled={pushData.isSilentPush}
            defaultValues={pushData.interruptionLevel ? [pushData.interruptionLevel] : []}
            onSelect={([interruptionLevelID]) =>
              setCurrentMediumField('interruptionLevel', interruptionLevelID)
            }
            Option={BasicOption}
            options={options}
            placeholder={SETTINGS_DISPLAY_CONTENT.placeholder}
            isFullWidth
          />
        </StyledTooltip>
      ) : (
        <SettingsReadOnlyText data-test="interruption-level-ro">
          {getReadOnlyText()}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
